'use strict';

import {Campus} from '@uw-it-sis/lib-react/lib/AppConstants';

/**
 * Utility methods for organization related stuff.
 */
const OrgUtils = {};

/**
 * A comparator for names which ignores inconsequential name prefixes like "The".
 */
OrgUtils.nameComparator = function(a, b) {
    let nameA = a.replace(/^(The |An |A )/i, "").replace(/\s\s+/g, ' ').toLowerCase();
    let nameB = b.replace(/^(The |An |A )/i, "").replace(/\s\s+/g, ' ').toLowerCase();

    if (nameA > nameB) {
        return 1
    }

    if (nameA < nameB) {
        return -1
    }

    return 0;
};

/**
 * A comparator for named entities (aka have a name property) which ignores inconsequential name prefixes like "The".
 * Uses the nameComparator method.
 */
OrgUtils.namedEntityComparator = function(a, b) {
    return (OrgUtils.nameComparator(a.name, b.name));
};

/**
 * Gets the campus code for the given campus name.
 * @param campusName The name of the campus (e.g. "seattle", "tacoma", "bothell").
 * @return The campus code as a string if the name is recognized. Otherwise, null.
 */
OrgUtils.getCampusCodeForName = function(campusName) {
    let campusCode = null;
    if (campusName) {
        switch(campusName.toLowerCase().trim()) {
            case Campus.seattle:
                campusCode = "306";
                break;
            case Campus.bothell:
                campusCode = "310";
                break;
            case Campus.tacoma:
                campusCode = "323";
                break;
        }
    }

    return campusCode;
};

/**
 * Gets the campus name for the given campus code.
 * @param campusCode The campus code as a string (e.g. "306", "310", "323").
 * @return The campus name as a string if the code is recognized. Otherwise, null.
 */
OrgUtils.getCampusNameForCode = function(campusCode) {
    let campusName = null;

    if (campusCode) {
        switch(campusCode.toLowerCase().trim()) {
            case "306":
                campusName = Campus.seattle;
                break;
            case "310":
                campusName = Campus.bothell;
                break;
            case "323":
                campusName = Campus.tacoma;
                break;
        }
    }

    return campusName;
};

/**
 * Returns a shortcode for the given page. Useful for logging or analytics.
 * @param url The url to get a code for
 * @param app The current app we're in.
 * @return A shortcode representing the page at the given url.
 */
OrgUtils.getPageFromUrl = function(url, app) {
    /*
     * Each key should be a regular expression
     * Each value should be a 2-4 letter shortcode
     *
     * Keys will be matched in the order they are given, so two keys are a
     * super/subset of each other, the more specific regex should go first.
     */
    let matches = {
        // COURSE
        '/course/#/courses$'            : 'CRS',
        '/course/#/courses/.*'          : 'CRD',
        '/course/#/bookmarks'           : 'CBM',
        // PROGRAM
        '/program/#/bookmarks'          : 'PBM',
        '/program/#/.*'                 : 'PGM',
        // PLAN
        '/plan/#/$'                     : 'AYV',
        '/plan/#/[0-9]{4}-[0-9]{2}'     : 'AYV',
        '/plan/#/(wi|au|su|sp)[0-9]{2}' : 'REGV',
        '/plan/#/[0-9]{4}[1-4][abAB]?'  : 'REGV',
        '/plan/#/schedule'              : 'SB',
        '/plan/#/print'                 : 'PRNT',
        '/plan/#/import'                : 'SOC',
        // AUDIT
        '/audit/#/degree'               : 'AUDG',
        '/audit/#/plan'                 : 'AUPL',
        '/audit/#/equivalency'          : 'AUEQ',
        // HOME
        '/home/#/home'                  : 'HOME',
        '/home/#/account'               : 'ACT',
    }

    /*
     * These are urls that map to the base path of an app.
     * E.g. the base url for app-plan is '/course' but going to
     * myplan.uw.edu/course redirects you to /course/#/courses so a link to '/course'
     * should return 'CRS'.
     *
     * There is a problem though, where a link to /plan would send you to app-plan if
     * you were in say app-home, but if you are in app-audit the link to /plan
     * would probably send you to /audit/#/plan
     * Because of this, we'll first check if which app we're in, and if there's
     * another page that makes more sense.
     *
     * NOTE: if we change the default page for a given app, we'll have to
     * change the stub here.
     */
    let appMatches = {
        '/plan'    : 'AYV',
        '/course'  : 'CRS',
        '/program' : 'PGM',
        '/audit'   : 'AUPL',
        '/home'    : 'HOME',
    }


    for (let regex in matches) {
        if (url.match(regex)) {
            return matches[regex];
        }
    }

    // If the given url is a relative path, we'll try to figure out where it
    // will go based on what app we're in.
    if (app && !url.includes('#')) {
        let appUrl = `/${app}/#${url}`

        for (let regex in matches) {
            if (appUrl.match(regex)) {
                return matches[regex];
            }
        }
    }

    // Maybe the link is to the root of another app? See if there's a match there.
    // This has to come after the app check so that a link to /plan from
    // app-audit will return AUPL instead of AYV
    for (let regex in appMatches) {
        if (url.match(regex)) {
            return appMatches[regex];
        }
    }


    return null;
}

export default OrgUtils;
